import React from "react";

import { useAsyncOptions } from "./useAsyncOptions";
import { useLazyLoadSentinel } from "./useLazyLoadSentinel";
import {
  flattenOptions,
  hasGroups,
  toBaseUiItems,
} from "src/components/Select/utils";
import type { SelectOption as MultiSelectOption } from "src/components/Select/types";

interface UseMultiSelectOptionsConfig {
  options?: MultiSelectOption[];
  loadOptions?: (
    inputValue: string
  ) => Promise<{ label: string; value: string; disabled?: boolean }[]>;
  defaultOptions?:
    | boolean
    | { label: string; value: string; disabled?: boolean }[];
  cacheOptions?: boolean;
  isAsyncLoadOptionEnabled?: boolean;
  fetchMore?: () => void;
  totalOptionsCount?: number;
}

export function useMultiSelectOptions(props: UseMultiSelectOptionsConfig) {
  const isAsync = !!props.loadOptions;

  const { asyncOptions, isLoading, handleAsyncInputChange } = useAsyncOptions({
    loadOptions: props.loadOptions,
    defaultOptions: props.defaultOptions ?? false,
    cacheOptions: props.cacheOptions ?? false,
  });

  const resolvedOptions: MultiSelectOption[] = isAsync
    ? asyncOptions
    : (props.options ?? []);
  const flatOptions = flattenOptions(resolvedOptions);
  const isGrouped = hasGroups(resolvedOptions);

  const baseUiItems = React.useMemo(
    () =>
      resolvedOptions.length > 0 ? toBaseUiItems(resolvedOptions) : undefined,
    [resolvedOptions]
  );

  const hasMoreLazy =
    (props.isAsyncLoadOptionEnabled ?? false) &&
    typeof props.totalOptionsCount === "number" &&
    flatOptions.length < props.totalOptionsCount;

  const loaderCallbackRef = useLazyLoadSentinel({
    fetchMore: props.fetchMore,
    isEnabled: props.isAsyncLoadOptionEnabled ?? false,
  });

  return {
    isAsync,
    resolvedOptions,
    flatOptions,
    isGrouped,
    isLoading,
    handleAsyncInputChange,
    baseUiItems,
    hasMoreLazy,
    loaderCallbackRef,
  };
}
